import React, { useContext } from 'react'
import Const from '../Consts'
import { AuthorInfo } from './AuthorInfo'
import { IndividualRating, OverallRating } from '../review_ratings'
import { ReviewReply } from './review_reply'
import { canarySupplierPath, supplierPagePath } from '../routes/path'
import type { Review as ReviewType, ReviewReply as ReviewReplyType } from '../supplier_show/prop_shapes/review'
import { AuthContext, canReportAbuse } from 'components/common/auth/AuthContext'
import clsx from 'clsx'
import { SupplierContext } from 'components/supplier_show/SupplierContext'
import { ReviewActionsMenu } from 'components/review_tiles/ReviewActionsMenu'
import { ReviewTileFooter } from 'components/review_tiles/ReviewTileFooter'
import type { ReviewTileRenderMode, IssueTrackingCTAMode, ReviewTileEditActionMode } from 'components/review_tiles/ReviewTileFooter'

export type { IssueTrackingCTAMode }

export type SupplierLinkMode = 'default' | 'canary'

const transformReviewReply = (serverReviewReply: any): ReviewReplyType => {
  const { id, text, updated_at: updatedAt } = serverReviewReply ?? {}
  const parsedDate = Date.parse(updatedAt)
  return {
    id,
    text,
    updatedAt: !Number.isNaN(parsedDate) ? new Date(updatedAt) : undefined
  }
}

interface ReviewTileProps {
  review: ReviewType
  displaySupplierName?: boolean
  noPadding?: boolean
  renderMode?: ReviewTileRenderMode
  onRaiseIssueClick?: () => void
  onActiveIssueClick?: () => void
  onIssueStatusChange?: () => void | Promise<void>
  issueTrackingCTAMode?: IssueTrackingCTAMode
  editActionMode?: ReviewTileEditActionMode
  supplierLinkMode?: SupplierLinkMode
}

export function ReviewTile ({
  review,
  displaySupplierName = false,
  noPadding = false,
  renderMode = 'default',
  onRaiseIssueClick,
  onActiveIssueClick,
  onIssueStatusChange,
  issueTrackingCTAMode,
  editActionMode,
  supplierLinkMode = 'default'
}: ReviewTileProps): JSX.Element {
  const { star_ratings: starRatings } = review

  const authContext = useContext(AuthContext)
  const showReportAbuse = canReportAbuse(authContext)

  const reviewReply = transformReviewReply(review.review_reply)

  const supplierSlug = review.supplier_slug ?? ''
  const contractVehicleSlug = review.contract_vehicle_slug
  const supplierHref = supplierLinkMode === 'canary' && contractVehicleSlug != null
    ? canarySupplierPath(contractVehicleSlug, supplierSlug)
    : supplierPagePath(supplierSlug)

  const supplierContext = {
    supplierId: review.supplier_id,
    supplierName: review.supplier_name,
    logoUrl: review.supplier_logo_url
  }

  return (
    <SupplierContext.Provider value={supplierContext}>
      <div
        className={clsx('pro-rounded pro-closest-shadow review-tile margin-bottom-2', review.status, noPadding && 'unpadded')}
        id={`review-${review.id}`}
      >
        <div className='review-actions-menu'>
          <ReviewActionsMenu review={review} canReportAbuse={showReportAbuse} />
        </div>
        <div className='grid-x'>
          <div className='large-auto medium-12 cell author-info'>
            {displaySupplierName && (
              <h3 className='supplier-name'>
                <a href={supplierHref}>{review.supplier_name}</a>
              </h3>
            )}
            <AuthorInfo
              review={review}
              renderMode={renderMode}
            />
          </div>
          <div className='large-shrink medium-12 cell'>
            <div className='grid-x grid-margin-x'>
              <div className='large-shrink medium-4 small-12 cell'>
                <OverallRating ratingLabel={Const.RATING_TYPES.overall_rating} value={starRatings.overall_rating} />
              </div>
              <div className='large-shrink medium-4 small-6 cell ratings'>
                <div className='grid-y full-height'>
                  <IndividualRating title={Const.RATING_TYPES.service_rating} value={starRatings.service_rating} />
                  <IndividualRating title={Const.RATING_TYPES.price_rating} value={starRatings.price_rating} />
                </div>
              </div>
              <div className='large-shrink medium-4 small-6 cell ratings'>
                <div className='grid-y full-height'>
                  <IndividualRating title={Const.RATING_TYPES.quality_rating} value={starRatings.quality_rating} />
                  <IndividualRating title={Const.RATING_TYPES.timeliness_rating} value={starRatings.timeliness_rating} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr />
        {review.title !== undefined && (
          <div className='pro-text-s font-bold color-pro-steel-grey-700 margin-bottom-1'>{review.title}</div>
        )}
        <div className='pro-text-s color-pro-steel-grey-700 review-body' data-testid={`review-body-${review.id}`}>
          {review.body}
        </div>
        <ReviewTileFooter
          review={review}
          renderMode={renderMode}
          onRaiseIssueClick={onRaiseIssueClick}
          onActiveIssueClick={onActiveIssueClick}
          onIssueStatusChange={onIssueStatusChange}
          issueTrackingCTAMode={issueTrackingCTAMode}
          editActionMode={editActionMode}
        />
        <div className='grid-y full-height'>
          <ReviewReply reply={reviewReply} reviewId={review.id} />
        </div>
      </div>
    </SupplierContext.Provider>
  )
}
